import React from "react";
import { Users, Trophy, BarChart3, Download, MessageSquare, Database, Settings, Plus, Minus, Mail, Send, Award } from "lucide-react";
import html2pdf from 'html2pdf.js';

export default function AdminGuide() {
  const handleDownloadPDF = () => {
    // Hide the button before generating PDF
    const button = document.querySelector('.download-button-container');
    if (button) button.style.display = 'none';
    
    const element = document.getElementById('guide-content');
    const opt = {
      margin: 0.5,
      filename: 'Golf-Eclectic-Admin-Guide.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    
    html2pdf().set(opt).from(element).save().then(() => {
      // Show button again after PDF is generated
      if (button) button.style.display = 'block';
    });
  };

  return (
    <div id="guide-content" className="max-w-4xl mx-auto space-y-8">
      {/* Welcome Section with Download Button */}
      <div className="text-center space-y-2">
        {/* Golf Eclectic Logo - Shows everywhere */}
        <img 
          src="/logo.png" 
          alt="Golf Eclectic Logo" 
          className="w-24 h-24 mx-auto rounded-full object-cover"
        />
        <h1 className="text-xl font-semibold text-ink tracking-tight">Admin Guide to Golf Eclectic</h1>
        <p className="text-lg text-ink-muted">Complete guide to managing competitions and players</p>
        
        {/* Download Button - Hidden in PDF */}
        <div className="download-button-container">
          <button
            onClick={handleDownloadPDF}
            className="mt-4 inline-flex items-center px-6 py-3 bg-ink hover:bg-ink-hover text-white font-semibold rounded-lg shadow-md transition-colors"
          >
            <Download className="w-5 h-5 mr-2" />
            Download as PDF
          </button>
        </div>
      </div>

      {/* Admin Navigation */}
      <section className="bg-accent-tint-2 border border-line rounded-lg p-6">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">🎯 Admin Navigation</h2>
        <p className="text-ink-muted mb-3">
          A persistent navigation bar is available on all admin pages for easy access:
        </p>
        <ul className="space-y-2 text-ink-muted">
          <li>• <span className="font-semibold">Dashboard:</span> Overview and quick actions</li>
          <li>• <span className="font-semibold">Email Players:</span> Send emails with attachments</li>
          <li>• <span className="font-semibold">Competitions:</span> Manage all competitions</li>
          <li>• <span className="font-semibold">Players:</span> Player management</li>
          <li>• <span className="font-semibold">Utilities:</span> Database and system tools</li>
          <li>• <span className="font-semibold">Logout:</span> Secure logout from any page</li>
        </ul>
      </section>

      {/* Competition Management */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Trophy className="w-6 h-6 text-warm" />
          Managing Competitions
        </h2>
        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Create Competition</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• Set name, start/end dates (UK format: DD/MM/YYYY)</li>
              <li>• Define total rounds allowed per player</li>
              <li>• <strong>💎 Excellent 2:</strong> Enable "Recognize Score of 2" to celebrate scores of 2</li>
              <li>• One round per player per day rule is enforced</li>
            </ul>
            <div className="mt-2 p-2 bg-accent-tint-2 border border-accent/30 rounded text-sm text-accent-deep">
              <strong>💎 Visual Indicator:</strong> Competitions with "Excellent 2 Mode" enabled show a green <span className="font-semibold">💎 Excellent 2 Mode</span> badge on their competition card.
            </div>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Competition View</h3>
            <p className="text-ink-muted mb-2">From the competition detail page, you can:</p>
            <ul className="space-y-1 text-ink-muted">
              <li>• View all registered players with their progress</li>
              <li>• <strong>View Rounds:</strong> See all rounds with trophy 🏆 and diamond 💎 indicators</li>
              <li>• <strong>View Best Scores:</strong> NEW! See player's hole-by-hole best scores with achievements</li>
              <li>• <strong>Enter Score:</strong> Input rounds on behalf of players with achievement detection</li>
              <li>• <strong>Email Top 3 Certificates:</strong> Send certificates to winners</li>
              <li>• View and manage the leaderboard</li>
              <li>• Generate reports and certificates</li>
              <li>• Email players individually or as a group</li>
              <li>• Post broadcast messages to all players</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Player Cards Display</h3>
            <p className="text-ink-muted mb-2">Each player card shows:</p>
            <ul className="space-y-1 text-ink-muted">
              <li>• Player name and registration status</li>
              <li>• Rounds completed vs. total allowed</li>
              <li>• Quick actions: View, Enter Score, View Best Scores</li>
              <li>• Withdrawn players are automatically hidden</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Score Entry */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Plus className="w-6 h-6 text-accent" />
          Score Entry
        </h2>
        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Admin Score Entry</h3>
            <p className="text-ink-muted mb-2">Identical interface to player score entry:</p>
            <ul className="space-y-1 text-ink-muted">
              <li>• +/- toggle buttons for each hole</li>
              <li>• Date selection (no default - must be selected)</li>
              <li>• Course handicap entry</li>
              <li>• Visual indicators: green for improvements, red for worse scores</li>
              <li>• Achievement highlights: yellow for hole-in-one 🏆, green for excellent 2 💎</li>
              <li>• Best scores displayed under each hole</li>
              <li>• Auto-detection of hole-in-ones (requires confirmation)</li>
              <li>• Auto-detection of scores of 2 in Excellent 2 competitions (requires confirmation)</li>
              <li>• Celebration messages posted on behalf of player</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Bulk Entry</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• Enter multiple rounds for the same player at once</li>
              <li>• Each round has its own date and handicap</li>
              <li>• Saves time when entering historical data</li>
              <li>• Validates all rounds before submission</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Validation Rules</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• Max score per hole: 10</li>
              <li>• All 18 holes must have scores (no zeros)</li>
              <li>• Date must be within competition period</li>
              <li>• One round per player per day</li>
              <li>• Cannot exceed total rounds limit</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Communication Features */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-accent-deep" />
          Player Communication (NEW!)
        </h2>
        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">🎙️ Broadcast Messages</h3>
            <p className="text-ink-muted mb-2">Available from Dashboard → Send Broadcast:</p>
            <ul className="space-y-1 text-ink-muted">
              <li>• Send messages to ALL players in the system</li>
              <li>• Title, content, and priority level (low/medium/high)</li>
              <li>• Messages appear on player dashboards</li>
              <li>• Great for system-wide announcements</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">📧 Email Players</h3>
            <p className="text-ink-muted mb-2">Accessible from Navigation → Email Players:</p>
            <ul className="space-y-1 text-ink-muted">
              <li>• Select individual players or all players in a competition</li>
              <li>• Rich text editor with formatting (bold, italic, lists)</li>
              <li>• Attach files (up to 10MB total, max 5 files)</li>
              <li>• Preview before sending</li>
              <li>• Sends via configured SMTP</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">📬 Competition Messages</h3>
            <p className="text-ink-muted mb-2">Post messages specific to a competition:</p>
            <ul className="space-y-1 text-ink-muted">
              <li>• Available from competition view page</li>
              <li>• Visible only to players in that competition</li>
              <li>• Set priority (normal/important)</li>
              <li>• Players see unread count badge</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Reports & Certificates */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Download className="w-6 h-6 text-accent-deep" />
          Reports & Certificates (NEW!)
        </h2>
        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">📊 Competition Reports</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• <strong>Excel Export:</strong> Full competition data with all players and rounds</li>
              <li>• <strong>Leaderboard PDF:</strong> Printable leaderboard with rankings</li>
              <li>• <strong>Player Report:</strong> Individual player performance details</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">🏆 Certificates</h3>
            <p className="text-ink-muted mb-2">Two certificate options:</p>
            <ul className="space-y-1 text-ink-muted">
              <li>• <strong>Top 3 Certificates (PDF):</strong> Generate single PDF with all 3 certificates for printing</li>
              <li>• <strong>Email Top 3:</strong> Generate individual PDFs and automatically email to 1st, 2nd, and 3rd place winners</li>
            </ul>
            <p className="text-ink-muted mt-2 text-sm">
              Certificates include player name, position, net score, gross score, handicap breakdown, competition details, and date issued.
            </p>
          </div>
        </div>
      </section>

      {/* Player Management */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Users className="w-6 h-6 text-accent-deep" />
          Player Management
        </h2>
        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">View All Players</h3>
            <p className="text-ink-muted mb-2">Access from Navigation → Players:</p>
            <ul className="space-y-1 text-ink-muted">
              <li>• Search and filter players</li>
              <li>• View player details and competition history</li>
              <li>• Merge duplicate accounts</li>
              <li>• Manual player registration (email/password)</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Player Withdrawals</h3>
            <p className="text-ink-muted mb-2">Players can withdraw themselves from competitions:</p>
            <ul className="space-y-1 text-ink-muted">
              <li>• All player data (rounds, scores, registration) is permanently deleted</li>
              <li>• Withdrawn players automatically removed from leaderboards</li>
              <li>• Players can rejoin later (start fresh)</li>
              <li>• Admin views automatically hide withdrawn players</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Admin Utilities */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Database className="w-6 h-6 text-red-600" />
          Admin Utilities
        </h2>
        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Database Cleanup</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• Clean orphaned records (rounds without players, etc.)</li>
              <li>• Recalculate all best scores</li>
              <li>• Renumber rounds for consistency</li>
              <li>• Database integrity checks</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">User Management</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• Delete users completely from system</li>
              <li>• Change admin password (self-service)</li>
              <li>• View user sessions and activity</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Archive & Backup</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• Archive old competitions</li>
              <li>• Export data for backup</li>
              <li>• Restore archived data</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Test Account Management System (NEW!) */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Users className="w-6 h-6 text-warm" />
          🧪 Test Account Management (PRODUCTION TESTING)
        </h2>
        <div className="bg-warm-tint/40 border-2 border-warm/40 rounded-lg p-6 mb-4">
          <h3 className="font-bold text-xl text-warm mb-3">⚠️ Critical for Production Testing</h3>
          <p className="text-ink mb-3">
            The Test Account Management System allows you to safely test new features in production without affecting real users. Test accounts and data are <strong>completely isolated</strong> from real users.
          </p>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">What are Test Accounts?</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• <strong>Test Admin:</strong> Can create and manage test competitions only</li>
              <li>• <strong>Test Players:</strong> Can only see and join test competitions</li>
              <li>• <strong>Isolation:</strong> Test data never visible to real users</li>
              <li>• <strong>Email Domain:</strong> Use @golfeclectic.net for test accounts</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Creating Test Accounts</h3>
            <ol className="space-y-2 text-ink-muted list-decimal list-inside">
              <li>Navigate to <strong>Admin Utilities → Test Account Management</strong></li>
              <li>Click <strong>"Create Test Account"</strong> button</li>
              <li>Fill in: Name, Email (@golfeclectic.net), Password</li>
              <li><strong>NEW:</strong> Use 👁️ eye icon to show/hide password while typing (prevents typos!)</li>
              <li>Toggle <strong>"Is Admin"</strong> if creating test admin</li>
              <li>Click <strong>"Create Account"</strong></li>
              <li>Account is flagged as test and immediately available</li>
            </ol>
            <div className="mt-3 p-3 bg-app border border-line rounded">
              <p className="text-sm text-accent-deep"><strong>Recommended Structure:</strong></p>
              <ul className="text-sm text-accent-deep mt-1 space-y-1">
                <li>• Admin: testadmin@golfeclectic.net (Password: TestAdmin2025!)</li>
                <li>• Players: tester1@golfeclectic.net, tester2@golfeclectic.net, etc. (Password: TestPlayer2025!)</li>
              </ul>
            </div>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">✏️ Edit Test Accounts (NEW!)</h3>
            <p className="text-ink-muted mb-3">Fix passwords, emails, or names without deleting accounts.</p>
            <ol className="list-decimal list-inside space-y-2 text-ink-muted">
              <li>Find account in Test Account Management list</li>
              <li>Click blue <strong>"Edit"</strong> button</li>
              <li>Update Name, Email, or Password as needed</li>
              <li><strong>Password field:</strong> Leave blank to keep current, enter new to change</li>
              <li>Use 👁️ eye icon to verify password before saving</li>
              <li>Click <strong>"Update Account"</strong> - changes apply immediately</li>
            </ol>
            <div className="mt-3 p-3 bg-accent-tint-2 border border-line rounded">
              <p className="text-sm text-ink"><strong>Common Use Cases:</strong></p>
              <ul className="text-sm text-ink mt-1 space-y-1">
                <li>• Fix password typo (login failing)</li>
                <li>• Update email address</li>
                <li>• Rename account for clarity</li>
                <li>• Change password without losing test data</li>
              </ul>
            </div>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Complete Data Isolation</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-accent-tint-2 border border-accent/30 rounded">
                <h4 className="font-semibold text-accent-deep mb-2">✅ Test Accounts CAN:</h4>
                <ul className="text-sm text-accent-deep space-y-1">
                  <li>• Create test competitions</li>
                  <li>• See only test competitions</li>
                  <li>• Add test players to test competitions</li>
                  <li>• Enter scores in test competitions</li>
                  <li>• Test all features safely</li>
                </ul>
              </div>
              <div className="p-3 bg-red-50 border border-red-300 rounded">
                <h4 className="font-semibold text-red-900 mb-2">❌ Test Accounts CANNOT:</h4>
                <ul className="text-sm text-red-800 space-y-1">
                  <li>• See real competitions</li>
                  <li>• Access real competition data</li>
                  <li>• Add players to real competitions</li>
                  <li>• Edit scores in real competitions</li>
                  <li>• Affect real users in any way</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Test Account Login</h3>
            <div className="p-3 bg-warm-tint/40 border border-warm/40 rounded">
              <p className="text-warm font-semibold mb-2">⚠️ Important: Login Location</p>
              <ul className="text-sm text-warm space-y-1">
                <li>• Test accounts log in at <strong>/login</strong> (player login page)</li>
                <li>• NOT at /admin/login (that's for main admin only)</li>
                <li>• Use <strong>email + password</strong> authentication</li>
                <li>• Test admins are redirected to admin dashboard after login</li>
              </ul>
            </div>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Managing Test Data</h3>
            <ul className="space-y-2 text-ink-muted">
              <li><strong>Reset Single Account:</strong> Clears all data (rounds, scores, messages) but keeps account credentials</li>
              <li><strong>Reset All Accounts:</strong> Clears all test data for all test accounts at once</li>
              <li><strong>🧪 Delete All Test Data:</strong> Bulk deletes ALL test competitions, rounds, scores, messages, and social posts (keeps test accounts) - One-click cleanup!</li>
              <li><strong>Delete Account:</strong> Permanently removes account and all data (cannot be undone)</li>
              <li><strong>Password Persistence:</strong> Reset and bulk delete preserve passwords, Delete removes them</li>
            </ul>
            <div className="mt-3 p-3 bg-app border border-line rounded text-sm text-accent-deep">
              <strong>💡 Quick Cleanup:</strong> Use "🧪 Delete All Test Data" button to remove all test competitions and data in one click after testing. Test accounts remain ready for next session.
            </div>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Production Testing Workflow</h3>
            <ol className="space-y-2 text-ink-muted list-decimal list-inside">
              <li><strong>Deploy new feature</strong> to production</li>
              <li><strong>Login as test admin</strong> (testadmin@golfeclectic.net at /login)</li>
              <li><strong>Create test competition</strong> (auto-flagged with 🧪 TEST)</li>
              <li><strong>Add test players</strong> to test competition</li>
              <li><strong>Test feature thoroughly</strong> in isolated sandbox</li>
              <li><strong>Verify functionality</strong> works correctly</li>
              <li><strong>Reset test data</strong> when done (optional)</li>
              <li><strong>Real users unaffected</strong> - never see test data</li>
            </ol>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">Visual Indicators (Real Admin View)</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• Test competitions show <span className="inline-flex items-center px-2 py-1 bg-warm text-warm font-semibold rounded text-sm">🧪 TEST</span> badge</li>
              <li>• Yellow border and background tint for test competitions</li>
              <li>• Real admin sees both test and real competitions</li>
              <li>• Test admin only sees test competitions (no badge needed)</li>
            </ul>
          </div>
        </div>

        <div className="mt-4 p-4 bg-accent-tint-2 border-2 border-accent/30 rounded-lg">
          <h3 className="font-bold text-accent-deep mb-2">📚 Detailed Guide Available</h3>
          <p className="text-accent-deep">
            For comprehensive test account setup, workflows, and troubleshooting, click <strong>"View Guide"</strong> in the Test Account Management section of Admin Utilities.
          </p>
        </div>
      </section>

      {/* Social Feed Moderation */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-accent" />
          Social Feed (NEW!)
        </h2>
        <div className="bg-white border border-line rounded-lg p-4">
          <p className="text-ink-muted mb-3">
            Players can now interact via a competition-specific social feed with posts, replies, and reactions.
          </p>
          <ul className="space-y-2 text-ink-muted">
            <li>• <span className="font-semibold">Player Control:</span> Each player can enable/disable social feed per competition in their settings</li>
            <li>• <span className="font-semibold">Content:</span> Posts limited to 500 characters</li>
            <li>• <span className="font-semibold">Reactions:</span> Like 👍, Love ❤️, Celebrate 🎉, Clap 👏</li>
            <li>• <span className="font-semibold">Moderation:</span> Players can delete their own posts; admin moderation features on backlog if needed</li>
            <li>• <span className="font-semibold">Privacy:</span> Only registered players in competition can view and post</li>
          </ul>
        </div>
      </section>

      {/* Best Practices */}
      <section className="bg-app border border-line rounded-lg p-6">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">💡 Admin Best Practices</h2>
        <ul className="space-y-2 text-ink-muted">
          <li>• <strong>Regular Backups:</strong> Export competition data regularly</li>
          <li>• <strong>Communication:</strong> Use broadcast messages for important announcements</li>
          <li>• <strong>Score Verification:</strong> Review leaderboards regularly for data accuracy</li>
          <li>• <strong>Player Support:</strong> Respond promptly to player queries</li>
          <li>• <strong>End of Season:</strong> Generate and email certificates to top performers</li>
          <li>• <strong>Database Maintenance:</strong> Run cleanup utilities periodically</li>
          <li>• <strong>Security:</strong> Change admin password regularly</li>
          <li>• <strong>Production Testing:</strong> Use test accounts to verify new features before real users see them</li>
          <li>• <strong>Test Data Cleanup:</strong> Reset test accounts between major test cycles</li>
        </ul>
      </section>

      {/* Scoring Rules Reference */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">📐 Scoring Rules Reference</h2>
        <div className="bg-white border border-line rounded-lg p-4">
          <ul className="space-y-2 text-ink-muted">
            <li>• <span className="font-semibold">Eclectic Score:</span> Best score achieved on each hole across all rounds</li>
            <li>• <span className="font-semibold">Gross Score:</span> Sum of all 18 best hole scores</li>
            <li>• <span className="font-semibold">Net Score:</span> Gross Score - (Course Handicap ÷ 2)</li>
            <li>• <span className="font-semibold">Leaderboard Ranking:</span> Based on Net Score (lowest wins)</li>
            <li>• <span className="font-semibold">Max Score Per Hole:</span> 10 strokes</li>
            <li>• <span className="font-semibold">Round Limit:</span> One round per player per day</li>
          </ul>
        </div>
      </section>

      {/* Support */}
      <section className="text-center bg-app rounded-lg p-6">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-3">Need Technical Support?</h2>
        <p className="text-ink-muted">
          For system issues, database problems, or feature requests, contact your system administrator 
          or refer to the technical documentation.
        </p>
      </section>
    </div>
  );
}
